import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, InputLabel, SxProps, TextField, Theme } from '@mui/material'
import { ChangeEventHandler, KeyboardEventHandler, useState } from 'react'
import { Device, DeviceHwID } from '../../types/models'
import { LinkTo } from '../atoms/LinkTo'
import { Response } from '../../types/api'

export interface ClaimDeviceFormProps {
  onSubmit: (hwId: string) => Promise<Response<Device>>
  sx?: SxProps<Theme>
}

export function ClaimDeviceForm(props: ClaimDeviceFormProps): React.ReactElement {

  const { sx } = props
  const { t } = useTranslation()
  const [deviceId, setDeviceId] = useState<DeviceHwID>('' as DeviceHwID)
  const [focused, setFocused] = useState(false)
  const [error, setError] = useState('')

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    let current = e.target.value.toUpperCase()
    if (current.length <= 9) {
      if (current.length >= 4 && current.indexOf('-') < 0) {
        current = current.substring(0, 4) + '-' + current.substring(4)
      }
      setDeviceId(current as DeviceHwID)
      setError('')
    }
  }

  const onKeyUp: KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.key === 'Backspace') {
      if (deviceId.length === 5) {
        setDeviceId(deviceId.substring(0, 4) as DeviceHwID)
      } else {
        setDeviceId(deviceId.substring(0, deviceId.length) as DeviceHwID)
      }
      e.preventDefault()
    }

    if (e.key === 'Enter') {
      onSubmit()
    }
  }

  const onKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
    }
  }

  const onSubmit = (): void => {
    const hwid = deviceId.replace('-', '') as DeviceHwID
    props.onSubmit(hwid).then(res => {
      if (!res.success) {
        setError(Object.values(res.messages)[0][0])
      }
    })
  }

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.2rem' }}>
        <InputLabel error={error !== ''} focused={focused}><Trans>Hardware ID</Trans></InputLabel>
        <LinkTo underline external url="https://www.enion.fi/en/hw-id" title={t('Where can I find my HWID?')}/>
      </Box>

      <TextField
        color="secondary"
        autoFocus
        required
        fullWidth
        data-cy="text-Hardware ID"
        error={error !== ''}
        helperText={error}
        placeholder='XXXX-XXXX'
        value={deviceId}
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        onChange={onChange}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        spellCheck={false}
        InputProps={{
          sx: {
            letterSpacing: '0.3rem'
          }
        }}
      />

      <Box sx={{ mt: '1rem', textAlign: 'right' }}>
        <Button
          data-cy="button-Next"
          disabled={!deviceId}
          onClick={onSubmit}
        >
          <Trans>Next</Trans>
        </Button>
      </Box>
    </Box>
  )
}
